import React, { useRef, useState } from 'react';
import PhoneInput, { CountryData } from "react-intl-tel-input";
import "react-intl-tel-input/dist/main.css";
import { CheckAddPlus, DropLeadingZero } from '../../../../utils/Formattingutil';
import { Config } from '../../../../Config/Config';
import { IsValidMobileNumber } from '../../../Utils/PhoneValidation';
import { GetValues } from '../../../../Config/MyAppConfig';
import { Api } from '../../../../Services/Api';
import { ShareBookingRequest } from '../../../../Services/BookingEntities';
import { appInsights } from "../../../../client";
import { PhoneToCnpBookingDetails } from '../../PhoneToCnpEntities';
import { ContentURL, getContentUrl } from '../../../Utils/ContentURL';
import './ShareViaSMS.scss';

interface ShareViaSMSProps {
    booking: PhoneToCnpBookingDetails;
}

export const ShareViaSMS: React.FC<ShareViaSMSProps> = ({ booking }) => {
    const [countryCode, setCountryCode] = useState("+61");
    const [contactNumber, setContactNumber] = useState("");
    const [isButtonActive, setIsButtonActive] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showAlreadySentError, setShowAlreadySentError] = useState(false);
    const phoneInputRef = useRef<HTMLInputElement>(null);

    const updateCode = (currentNumber: string, value: CountryData) => {
        validatePhone();
        setCountryCode(CheckAddPlus(value.dialCode));
    };

    const validatePhone = () => {
        const value = phoneInputRef.current?.value ?? "";
        setIsButtonActive(value.length >= 8);
        setIsSent(false);
        
        const numberOnlyRegex = /^[0-9\b]+$/;
        if (value === '' || numberOnlyRegex.test(value)) {
            setContactNumber(value);
        }
        setErrorMessage("");
        setShowAlreadySentError(false);
    };

    const isLandlineNumber = (phoneNumber: string): boolean => {
        // Check if it's an Australian landline (starts with 02, 03, 07, 08)
        const landlinePattern = /^\+61[2378]/;
        return landlinePattern.test(phoneNumber);
    };

    const sendSMS = async () => {
        setErrorMessage("");
        setShowAlreadySentError(false);

        const fullNumber = countryCode + DropLeadingZero(contactNumber);

        if (!isButtonActive) {
            setErrorMessage("Enter a valid Australian mobile number");
            return;
        }

        if (isSent) {
            setShowAlreadySentError(true);
            return;
        }

        // Check if it's a landline first
        if (isLandlineNumber(fullNumber)) {
            setErrorMessage("Landline numbers not supported");
            return;
        }

        if (IsValidMobileNumber(fullNumber)) {
            setIsSending(true);

            const shareBookingPayload: ShareBookingRequest = {
                BookingID: booking.BookingId,
                DispatchBookingID: booking.DispatchBookingId.toString(),
                HashCode: booking.ReadAccessCode,
                PhoneNumber: fullNumber,
                BookingDateTime: booking.Timing.LocalTime ?? booking.Timing.Time ?? "",
                ApiVersion: 1,
                IsVoiceChannelBooking: true,
                PassengerName: booking.PassengerName,
                DispatchSystemId: booking.DispatchSystemId
            };

            try {
                const result = await Api.Booking.ShareBooking(shareBookingPayload);

                if (result.isSuccess) {
                    appInsights.trackEvent({ name: "Shared booking successfully!" });
                    setIsSending(false);
                    setIsSent(true);
                    setContactNumber("");
                    setIsButtonActive(false);
                } else {
                    appInsights.trackEvent({ name: "Share booking failed!" }, { message: result.outcome });
                    setIsSending(false);
                    setErrorMessage("Sending SMS failed. Please try again.");
                }
            } catch (error) {
                setIsSending(false);
                setErrorMessage("Sending SMS failed. Please try again.");
            }
        } else {
            setErrorMessage("Enter a valid Australian mobile number");
        }
    };

    const shareBtnClass = `share-btn ${isButtonActive ? 'share-active-btn' : ''}`;

    return (
        <div className="share-via-sms">
            <p className="send-via-sms-info">Share via SMS</p>
            <div className="sms-row">
                <div className="send-via-sms-section">
                    <div className="country-code">
                        <PhoneInput
                            defaultCountry={'au'}
                            separateDialCode={true}
                            fieldId={"shareBookingIvrPhoneInput"}
                            value={countryCode}
                            onSelectFlag={updateCode}
                            preferredCountries={GetValues().AllowedCountryCodes}
                        />
                    </div>
                    <div className="share-mobile-number">
                        <input
                            ref={phoneInputRef}
                            type="tel"
                            placeholder="Enter phone number"
                            maxLength={Config.ContactNumberMaxLength}
                            onChange={validatePhone}
                            value={contactNumber}
                            autoFocus
                        />
                    </div>
                </div>
                <button className={shareBtnClass} onClick={sendSMS} disabled={isSending}>
                    {isSending ? (
                        <>
                            <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="share-spinner">
                                <path d="M1.63612 2.63579C5.15076 -0.878637 10.849 -0.87856 14.3637 2.63579C17.8784 6.15051 17.8784 11.8496 14.3637 15.3643C10.849 18.8787 5.15076 18.8787 1.63612 15.3643C1.24566 14.9738 1.24579 14.3408 1.63612 13.9502C2.02665 13.5597 2.65966 13.5597 3.05019 13.9502C5.78377 16.6836 10.216 16.6836 12.9496 13.9502C15.6833 11.2166 15.6833 6.78353 12.9496 4.04986C10.216 1.31655 5.78377 1.31647 3.05019 4.04986C2.65966 4.44038 2.02665 4.44038 1.63612 4.04986C1.24578 3.65932 1.24566 3.02626 1.63612 2.63579Z" fill="black"/>
                            </svg>
                            Share
                        </>
                    ) : (
                        <>
                            <svg width="22" height="23" viewBox="0 0 22 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 19.0001C20 17.8955 19.1046 17.0001 18 17.0001C17.3046 17.0001 16.6924 17.3551 16.334 17.8936C16.3191 17.9272 16.3031 17.9607 16.2842 17.9932C16.2651 18.026 16.2438 18.0572 16.2217 18.087C16.0808 18.3609 16 18.6709 16 19.0001C16 20.1046 16.8954 21.0001 18 21.0001C19.1046 21.0001 20 20.1046 20 19.0001ZM8 12.0001C8 10.8955 7.10457 10.0001 6 10.0001C4.89543 10.0001 4 10.8955 4 12.0001C4 13.1046 4.89543 14.0001 6 14.0001C7.10457 14.0001 8 13.1046 8 12.0001ZM20 5.00006C20 3.89549 19.1046 3.00006 18 3.00006C16.8954 3.00006 16 3.89549 16 5.00006C16 6.10463 16.8954 7.00006 18 7.00006C19.1046 7.00006 20 6.10463 20 5.00006ZM22 5.00006C22 7.2092 20.2091 9.00006 18 9.00006C16.8885 9.00006 15.8831 8.54642 15.1582 7.81451L9.84863 10.9122C9.94625 11.2582 10 11.6228 10 12.0001C10 12.3766 9.94685 12.7406 9.84961 13.086L15.1621 16.1817C15.8867 15.4521 16.8905 15.0001 18 15.0001C20.2091 15.0001 22 16.7909 22 19.0001C22 21.2092 20.2091 23.0001 18 23.0001C15.7909 23.0001 14 21.2092 14 19.0001C14 18.6215 14.0531 18.2554 14.1514 17.9083L8.84082 14.8135C8.11591 15.5454 7.11153 16.0001 6 16.0001C3.79086 16.0001 2 14.2092 2 12.0001C2 9.79092 3.79086 8.00006 6 8.00006C7.11103 8.00006 8.11598 8.4533 8.84082 9.18463L14.1504 6.08698C14.053 5.7413 14 5.37689 14 5.00006C14 2.79092 15.7909 1.00006 18 1.00006C20.2091 1.00006 22 2.79092 22 5.00006Z" fill="#1A1A1A" />
                            </svg>
                            Share
                        </>
                    )}
                </button>
            </div>

            <p className="hint-text">Australian mobile numbers only</p>
            {errorMessage && <p className="share-error">{errorMessage}</p>}

            {showAlreadySentError && (
                <div className="error-banner">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 1.33334C4.324 1.33334 1.33333 4.324 1.33333 8.00001C1.33333 11.676 4.324 14.6667 8 14.6667C11.676 14.6667 14.6667 11.676 14.6667 8.00001C14.6667 4.324 11.676 1.33334 8 1.33334ZM8 9.33334C7.63133 9.33334 7.33333 9.03534 7.33333 8.66667V5.33334C7.33333 4.96467 7.63133 4.66667 8 4.66667C8.36867 4.66667 8.66667 4.96467 8.66667 5.33334V8.66667C8.66667 9.03534 8.36867 9.33334 8 9.33334ZM8.66667 11.3333H7.33333V10H8.66667V11.3333Z" fill="#DC2626" />
                    </svg>
                    <span>SMS already sent to this number.</span>
                </div>
            )}

            {isSent && (
                <div className="success-banner">
                    <img src={getContentUrl(ContentURL.images.GreenCheckMark)} alt="Success" />
                    <span>SMS shared successfully</span>
                </div>
            )}
        </div>
    );
};
